import {
  setPlugins,
  plugin_crop,
  plugin_filter,
  plugin_finetune,
  plugin_annotate,
  plugin_frame,
  locale_en_gb,
  plugin_crop_locale_en_gb,
  plugin_filter_locale_en_gb,
  plugin_finetune_locale_en_gb,
  plugin_annotate_locale_en_gb,
  plugin_frame_locale_en_gb,
  markup_editor_locale_en_gb,
  markup_editor_defaults,
  plugin_finetune_defaults,
  plugin_filter_defaults,
  plugin_frame_defaults,
  createMarkupEditorToolbar,
  createDefaultShapePreprocessor,
} from '@messenger/pintura'
import { PinturaEditor } from '@messenger/pintura/react'
import '@messenger/pintura/pintura.css'

import { useRef, useState } from 'react'
import * as S from './Pintura.styled'
import { editBackgroundImageAtom, showPinturaAtom } from 'atoms'
import usePintura from './usePintura'
import { BackgroundRemovalTool } from './BackgroundRemoval/BackgroundRemovalTool'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { isImagePlaceholder } from 'utils/editor/imagePlaceholder'
import { withCorsQuery } from '@/utils/editor/cutoutImageReader'
import { useTranslation } from 'react-i18next'
import pinturaEn from '@messenger/pintura/locales/en/pintura.json'
import pinturaEs from '@messenger/pintura/locales/es/pintura.json'
import pinturaFr from '@messenger/pintura/locales/fr/pintura.json'
import { DEFAULT_LOCALE_CODE } from '@messenger/consts'

setPlugins(
  plugin_crop,
  plugin_filter,
  plugin_finetune,
  plugin_annotate,
  plugin_frame,
)

const pinturaLocales = {
  en: pinturaEn,
  es: pinturaEs,
  fr: pinturaFr,
}

const getPinturaLocale = (lng) => {
  const pinturaLang = pinturaLocales[lng] || {}
  const t = (key, params) => {
    let str = pinturaLang[key] || locale_en_gb[key] || key
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        str = str.replace(`{${k}}`, v)
      })
    }
    return str
  }

  const statusLabelLoadImage = (state) => {
    if (!state?.task) {
      return t('statusWaitingForImage')
    }

    if (state.error) {
      if (state.error.code === 'IMAGE_TOO_SMALL') {
        return t('statusErrorImageTooSmall', {
          minWidth: state.error.minWidth,
          minHeight: state.error.minHeight,
        })
      }
      return t('statusErrorLoadingImage')
    }

    if (state.task === 'blob-to-bitmap') {
      return t('statusCreatingPreview')
    }

    return t('statusLoadingImage')
  }

  const statusLabelProcessImage = (state) => {
    if (!state?.task) {
      return undefined
    }

    if (state.task === 'store') {
      if (state.error) {
        return t('statusErrorUploadingImage')
      }
      return t('statusUploadingImage')
    }

    if (state.error) {
      return t('statusErrorProcessingImage')
    }

    return t('statusProcessingImage')
  }

  const merged = {
    ...locale_en_gb,
    ...plugin_crop_locale_en_gb,
    ...plugin_filter_locale_en_gb,
    ...plugin_finetune_locale_en_gb,
    ...plugin_annotate_locale_en_gb,
    ...plugin_frame_locale_en_gb,
    ...markup_editor_locale_en_gb,
    ...pinturaLang,
    statusLabelLoadImage,
    statusLabelProcessImage,
  }

  // labelSupportError as before
  if (typeof merged.labelSupportError !== 'function') {
    merged.labelSupportError = locale_en_gb.labelSupportError
  }

  return merged
}

const editorConfigBase = {
  ...markup_editor_defaults,
  ...plugin_finetune_defaults,
  ...plugin_frame_defaults,
  ...plugin_filter_defaults,
  shapePreprocessor: createDefaultShapePreprocessor(),
  imageCropAspectRatio: undefined,
  cropImageSelectionCornerStyle: 'hook',
  // locale will be set dynamically
  // Specific tools for annotate (we don't want text)
  markupEditorToolbar: createMarkupEditorToolbar([
    'sharpie',
    'eraser',
    'line',
    'arrow',
    'rectangle',
    'ellipse',
  ]),
}

export const Pintura = ({ update }) => {
  const editBackground = useRecoilValue(editBackgroundImageAtom)
  const setShowPintura = useSetRecoilState(showPinturaAtom)
  const {
    url,
    src,
    imageReader,
    imageWriter,
    edits,
    handleFailure,
    handleProcessing,
    handleDoneEditing,
    caseImageId,
    applyAiVariant,
    appliedCutout,
    showOriginalPhoto,
  } = usePintura(update, editBackground)
  const { i18n } = useTranslation()
  const lng = i18n.language?.split('-')[0] || DEFAULT_LOCALE_CODE

  // Pintura builds its own DOM inside the wrapper. The Remove Background
  // tool portals into that DOM, so it needs the editor root once the image
  // (and with it the util rail) has loaded.
  const wrapperRef = useRef(null)
  const [editorRoot, setEditorRoot] = useState(null)

  const pinturaState = {
    ...editorConfigBase,
    locale: getPinturaLocale(lng),
    imageReader,
    imageWriter,
    src: withCorsQuery(src),
    imageState: edits,
    onProcesserror: () => handleFailure('process'),
    onProcessstart: handleProcessing,
    onProcess: handleDoneEditing,
    onLoaderror: () => handleFailure('load'),
    onLoad: () =>
      setEditorRoot(wrapperRef.current?.querySelector('.PinturaRoot') ?? null),
  }

  const showPintura = Boolean(
    url && Object.keys(editorConfigBase).length > 0 && !isImagePlaceholder(url),
  )

  return (
    <S.Wrapper
      ref={wrapperRef}
      width="70vw"
      maxWidth="1000px"
      height="100vh"
      minHeight="700px"
      maxHeight="800px"
      padding="12px 36px 36px 36px"
    >
      {showPintura && <PinturaEditor {...pinturaState}></PinturaEditor>}
      {showPintura && !editBackground && (
        <BackgroundRemovalTool
          editorRoot={editorRoot}
          imageUrl={url}
          caseImageId={caseImageId}
          appliedCutout={appliedCutout}
          onApply={applyAiVariant}
          onUseOriginal={showOriginalPhoto}
          onDone={() => setShowPintura(false)}
        />
      )}
    </S.Wrapper>
  )
}

export default Pintura
